// src/hooks/useAuth.js
//
// После разделения регистраций режим (shop/customer) определяется типом учётки
// (User.role), а не выбором пользователя в кабинете. Поэтому:
//   - selectMode больше не нужен (роль фиксирована учёткой)
//   - login требует параметр role — какую учётку открываем по этому email
//   - mode выводится из user (user.accountRole) или из роли сотрудника

import { useState, useEffect, createContext, useContext } from 'react';
import { auth as authApi, clearDataCache } from '../api';
import { clearMyShop } from './useMyShop';

const AuthContext = createContext(null);
const GUEST_KEY = 'alsatuu:guest';

function makeCanFn(user) {
  return (perm) => {
    if (!user) return false;
    if (user.role === 'owner') return true;       // у владельца всё
    if (user.role !== 'employee') return false;   // покупатель не имеет прав на магазин

    const perms = user.permissions || {};
    // billing — особый случай: даже у админа недоступен
    if (perm === 'billing') return perms.billing === true;
    // permissions.all === true означает «всё разрешено» (роль admin)
    if (perms.all === true) return true;
    return !!perms[perm];
  };
}

// Определяем активный mode по объекту пользователя.
//   - сотрудник → 'shop'
//   - учётка типа shop (accountRole=shop или старое role=owner) → 'shop'
//   - всё остальное → 'customer'
function deriveMode(u) {
  if (!u) return null;
  if (u.role === 'employee') return 'shop';
  if (u.accountRole === 'shop' || u.role === 'owner') return 'shop';
  return 'customer';
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = загружается
  const [mode, setMode] = useState(null);      // 'shop' | 'customer' | 'guest'
  const [blockedInfo, setBlockedInfo] = useState(null); // { kind, reason } если заблокирован

  // Слушаем глобальное событие blocked от API клиента — если в процессе работы
  // сервер вернул 403 blocked, сразу разлогиниваем и показываем экран блокировки
  useEffect(() => {
    const handler = (e) => {
      setBlockedInfo(e.detail || { kind: 'user', reason: null });
      // Токен оставляем — но пользователя сбрасываем, чтобы экран блокировки показался
      // (если разлогиним полностью, то после "Понятно" попадёт на стартовый экран, что и нужно)
    };
    window.addEventListener('auth:blocked', handler);
    return () => window.removeEventListener('auth:blocked', handler);
  }, []);

  // При старте — проверяем токен или гостевой флаг
  useEffect(() => {
    const token = localStorage.getItem('token');
    const isGuest = localStorage.getItem(GUEST_KEY) === '1';

    if (!token) {
      if (isGuest) {
        setUser(null);
        setMode('guest');
      } else {
        setUser(null);
      }
      return;
    }

    authApi.me()
      .then(u => {
        setUser(u);
        setMode(deriveMode(u));
      })
      .catch((e) => {
        // Если это 403 blocked — событие уже выстрелило, blockedInfo установлен
        // Токен НЕ удаляем (пусть остаётся, чтобы при разблокировке войти без перелогина)
        if (e && e.blocked) {
          setUser(null);
          return;
        }
        localStorage.removeItem('token');
        setUser(null);
      });
  }, []);

  // Универсальная установка пользователя после login/register
  const setAuthedUser = async (data) => {
    localStorage.setItem('token', data.token);
    localStorage.removeItem(GUEST_KEY);
    let fullUser;
    try {
      fullUser = await authApi.me();
    } catch (e) {
      fullUser = data.user;
    }
    setUser(fullUser);
    setMode(deriveMode(fullUser));
    return fullUser;
  };

  // Вход — role обязателен (выбирается на стартовом экране).
  const login = async (email, password, role) => {
    try {
      const data = await authApi.login(email, password, role);
      return setAuthedUser(data);
    } catch (e) {
      // 403 blocked — событие auth:blocked уже выстрелило в api/index.js,
      // но т.к. blockedInfo выставляется через addEventListener асинхронно,
      // ставим явно тут чтобы экран блокировки показался немедленно
      if (e && e.blocked) {
        setBlockedInfo(e.blocked);
      }
      throw e;
    }
  };

  // Раздельная регистрация
  const registerCustomer = async (email, password, name) => {
    const data = await authApi.registerCustomer(email, password, name);
    return setAuthedUser(data);
  };

  const registerShop = async (email, password, name) => {
    const data = await authApi.registerShop(email, password, name);
    return setAuthedUser(data);
  };

  // Вход сотрудника
  const loginEmployee = async (shopId, credential, password) => {
    let data;
    try {
      data = await authApi.employeeLogin(shopId, credential, password);
    } catch (e) {
      if (e && e.blocked) {
        setBlockedInfo(e.blocked);
      }
      throw e;
    }
    localStorage.setItem('token', data.token);
    localStorage.removeItem(GUEST_KEY);
    let fullUser;
    try {
      fullUser = await authApi.me();
    } catch (e) {
      fullUser = {
        id: data.employee.id,
        name: data.employee.name,
        role: 'employee',
        shopId: data.employee.shopId,
        permissions: data.employee.permissions || {},
      };
    }
    setUser(fullUser);
    setMode('shop');
    return fullUser;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem(GUEST_KEY);
    clearMyShop();
    clearDataCache();
    setUser(null);
    setMode(null);
    setBlockedInfo(null);
  };

  // Закрыть экран блокировки и вернуться к выбору режима.
  // Токен ОЧИЩАЕМ — пользователь должен залогиниться заново
  // (если разблокировали — спокойно войдёт).
  const clearBlocked = () => {
    localStorage.removeItem('token');
    localStorage.removeItem(GUEST_KEY);
    clearMyShop();
    clearDataCache();
    setUser(null);
    setMode(null);
    setBlockedInfo(null);
  };

  const enterAsGuest = () => {
    localStorage.setItem(GUEST_KEY, '1');
    setUser(null);
    setMode('guest');
  };

  const exitGuest = () => {
    localStorage.removeItem(GUEST_KEY);
    setUser(null);
    setMode(null);
  };

  const updateProfile = async (data) => {
    const updated = await authApi.updateMe(data);
    setUser(prev => ({ ...prev, ...updated }));
    return updated;
  };

  // Перечитать пользователя с сервера (например, после создания магазина —
  // чтобы роль стала owner и интерфейс владельца появился без перелогина).
  const refreshUser = async () => {
    try {
      const u = await authApi.me();
      setUser(u);
      setMode(deriveMode(u));
      return u;
    } catch (e) {
      return user;
    }
  };

  const isOwner    = user?.role === 'owner';
  const isEmployee = user?.role === 'employee';
  const isAdmin    = user?.role === 'employee' && user?.permissions?.all === true;
  const can        = makeCanFn(user);

  return (
    <AuthContext.Provider value={{
      user,
      mode,
      login,
      loginEmployee,
      registerCustomer,
      registerShop,
      logout,
      enterAsGuest,
      exitGuest,
      updateProfile,
      refreshUser,
      loading: user === undefined,
      isGuest: mode === 'guest',
      isOwner,
      isEmployee,
      isAdmin,
      can,
      blockedInfo,
      clearBlocked,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
